import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useOfflineToggle } from '../OfflineToggle/useOfflineToggle';
import { getLocalGrid } from '../../utils/localGrids';
import './SyncGridModal.css';

/**
 * Offers to sync the open local grid whenever syncing is possible.
 *
 * A local grid has no server counterpart until the user explicitly syncs it,
 * and the only sync affordance today lives on the dashboard. This hook surfaces
 * the same offer inside the editor, at the two moments the grid *could* be
 * synced: opening it with a working connection, and regaining a connection
 * while it is open.
 *
 * All prompt state lives here so MainLayout gains one hook call rather than
 * another stateful effect.
 */
export function useReconnectSyncPrompt({ id, isLocalGrid, isContentLoading }) {
  const { offline } = useOfflineToggle();
  const [syncDoc, setSyncDoc] = useState(null);

  // A confirm dialog is currently on screen.
  const isConfirmOpenRef = useRef(false);
  // The mount trigger has had its one chance this session.
  const hasPromptedOnMountRef = useRef(false);
  // Previous effective-offline value, for detecting the transition.
  const wasOfflineRef = useRef(offline);

  const promptToSync = useCallback(() => {
    // Never stack prompts. `syncDoc` covers both "modal open" and "sync in
    // flight", since the modal stays mounted for the whole upload.
    if (isConfirmOpenRef.current || syncDoc) return;

    // Sheets are written to localStorage on every change, so the stored record
    // is already current — no flush needed before reading it.
    const record = getLocalGrid(id);
    // Deleted in another tab — nothing to offer, and an error here would be noise.
    if (!record) return;

    isConfirmOpenRef.current = true;
    Modal.confirm({
      title: 'Sync this grid?',
      // antd's default here is an orange warning glyph, which reads as an error.
      // This is an opportunity, not a problem — brand-blue upload disc instead.
      icon: (
        <span className="sync-confirm-icon">
          <CloudUploadOutlined />
        </span>
      ),
      content: (
        <>
          <p className="sync-confirm-text">
            You're online, so this grid can be uploaded to the server.
          </p>
          <p className="sync-confirm-note">
            It will then be available on every device, and will leave your local
            grids.
          </p>
        </>
      ),
      okText: 'Sync',
      cancelText: 'Not now',
      centered: true,
      className: 'sync-confirm-modal',
      okButtonProps: { className: 'export-modal-btn-download' },
      cancelButtonProps: { className: 'export-modal-btn-cancel' },
      onOk: () => {
        isConfirmOpenRef.current = false;
        setSyncDoc(record);
      },
      onCancel: () => {
        isConfirmOpenRef.current = false;
      },
    });
  }, [id, syncDoc]);

  // Trigger A — mount. Waits for the grid to load so the confirm never lands on
  // top of the loader. Fires at most once per mounted editor session; if we are
  // offline the reconnect trigger takes over instead.
  useEffect(() => {
    if (!isLocalGrid || !id) return;
    if (isContentLoading) return;
    if (hasPromptedOnMountRef.current) return;
    hasPromptedOnMountRef.current = true;
    if (offline) return;
    promptToSync();
  }, [isLocalGrid, id, isContentLoading, offline, promptToSync]);

  // `promptToSync` changes identity whenever syncDoc does. Holding it in a ref
  // keeps the effect below keyed on connectivity alone, so an unrelated
  // rerender can never swallow or fabricate a transition.
  const promptRef = useRef(promptToSync);
  promptRef.current = promptToSync;

  // Trigger B — reconnect. Every offline→online transition prompts, including
  // the manual offline toggle being switched off. Deliberately not gated by the
  // mount trigger's ref: declining at mount and genuinely reconnecting later
  // are different moments and both deserve the offer.
  useEffect(() => {
    const wasOffline = wasOfflineRef.current;
    wasOfflineRef.current = offline;
    if (!isLocalGrid || !id) return;
    if (!wasOffline || offline) return;
    promptRef.current();
  }, [offline, isLocalGrid, id]);

  const closeSyncModal = useCallback(() => setSyncDoc(null), []);

  // A hard navigation, deliberately: `?local=true` bypasses the auth gate and
  // the "is this local?" checks are memoized with [] deps, so only a full page
  // load brings the grid back up server-backed with its socket room.
  const handleSynced = useCallback((spreadsheetId) => {
    setSyncDoc(null);
    window.location.href = `/milegrid/${spreadsheetId}`;
  }, []);

  return { syncDoc, closeSyncModal, handleSynced };
}

export default useReconnectSyncPrompt;
